import React, { useEffect, useRef, useState } from "react";
import Navbar from "../Components/Navbar";
import axios from "axios";
import Cookies from "js-cookie";
import { useAuthContext } from "../Contexts/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { AiFillWarning } from "react-icons/ai";
import { toast } from "react-toastify";
import loader from "../assets/loading.gif";

const API = process.env.REACT_APP_BASE_URL;

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

const authHeaders = () => ({
  headers: { "Content-Type": "application/json", token: Cookies.get("token") },
});

/** "1.234,56" -> 1234.56 */
const parseEs = (v) => {
  if (typeof v === "number") return v;
  return parseFloat(String(v || "0").replaceAll(".", "").replace(",", ".")) || 0;
};

/** 1234.56 -> "1.234,56" */
const fmtEs = (n, decimals = 2) =>
  Number(n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

const MIN_AMOUNT = 5;
const MAX_AMOUNT = 3000;

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

const Payment = () => {
  const navigate = useNavigate();
  const { userdata, user } = useAuthContext();

  // ---- corridor / quote ----------------------------------------------
  const [bootLoading, setBootLoading] = useState(true);
  const [corridorReady, setCorridorReady] = useState(false);
  const [corridorMsg, setCorridorMsg] = useState("");
  const [purposes, setPurposes] = useState([]);
  const [banks, setBanks] = useState([]);
  const [location, setLocation] = useState(null);

  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const quoteTimer = useRef(null);

  // ---- beneficiary ----------------------------------------------------
  // idLocation IS the bank — each Venezuelan bank is its own PMI location
  const [bene, setBene] = useState({
    bname: "",
    cedula: "",
    idLocation: "",
    bAccnumber: "",
    Rphone: "",
  });
  const [cuenta, setCuenta] = useState(true); // true = cuenta (20), false = pago móvil (11)
  const [savedBenes, setSavedBenes] = useState([]);
  const [beneModal, setBeneModal] = useState(false);

  // ---- identity document (front + back) --------------------------------
  // Files are held locally until the user hits submit — uploading on pick left
  // them with no forward action once both were chosen.
  const [docStatus, setDocStatus] = useState(null); // server state: { front, back, backRequired, complete }
  const [docType, setDocType] = useState("");                          // dni | nie | per | pass
  const [picked, setPicked] = useState({ front: null, back: null });   // File objects
  const [previews, setPreviews] = useState({ front: null, back: null }); // object URLs
  const [docUploading, setDocUploading] = useState(false);
  const [docModal, setDocModal] = useState(false);
  const frontInput = useRef(null);
  const backInput = useRef(null);

  // ---- order ----------------------------------------------------------
  const [purpose, setPurpose] = useState("fami");
  const [terms, setTerms] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ---- notify-me (service paused fallback) ----------------------------
  const [notifyEmail, setNotifyEmail] = useState(userdata?.email || "");
  const [notifySent, setNotifySent] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);

  /* ---------------------------------------------------------------- *
   * Boot: corridor health + catalog + saved beneficiaries
   * ---------------------------------------------------------------- */
  useEffect(() => {
    window.scrollTo(0, 0);

    const boot = async () => {
      try {
        const { data: health } = await axios.get(`${API}/api/pmi/health`);
        // setCorridorReady(Boolean(health?.corridorReady));
        setCorridorReady(false);

        setCorridorMsg(
            "El servicio de envíos se está reactivando. Estaremos disponibles en breve."
          );

          return;
        if (!health?.corridorReady) {
          setCorridorMsg(
            "El servicio de envíos se está reactivando. Estaremos disponibles en breve."
          );
          setBootLoading(false);
          return;
        }

        const [{ data: catalog }, { data: bankList }] = await Promise.all([
          axios.get(`${API}/api/pmi/catalog`),
          axios.get(`${API}/api/pmi/banks`),
        ]);
        setPurposes(catalog?.purposes || []);
        setBanks(bankList?.banks || []);

        if (Cookies.get("token")) {
          try {
            const { data: st } = await axios.get(
              `${API}/api/pmi/document/status`,
              authHeaders()
            );
            setDocStatus(st);
            if (st?.docType) setDocType(st.docType);
          } catch (_) {
            /* non-fatal */
          }

          try {
            const { data: benes } = await axios.get(
              `${API}/api/orders/userbenes`,
              authHeaders()
            );
            const list = (benes?.orders || []).map((e) => ({
              id: e._id,
              bname: e.bname,
              bbank: e.bbank,
              bAccnumber: e.bAccnumber,
              cedula: e.cedula,
            }));
            // de-dupe on bank + account + cédula
            const seen = new Set();
            setSavedBenes(
              list.filter((e) => {
                const k = `${e.bbank}|${e.bAccnumber}|${e.cedula}`;
                if (seen.has(k)) return false;
                seen.add(k);
                return true;
              })
            );
          } catch (_) {
            /* non-fatal */
          }
        }
      } catch (err) {
        console.error(err);
        setCorridorReady(false);
        setCorridorMsg(
          "No podemos conectar con el servicio de envíos en este momento."
        );
      } finally {
        setBootLoading(false);
      }
    };

    boot();
  }, []);

  /* ---------------------------------------------------------------- *
   * Live quote (debounced)
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!corridorReady) return;

    const value = parseEs(amount);
    if (!value || value < MIN_AMOUNT) {
      setQuote(null);
      return;
    }

    clearTimeout(quoteTimer.current);
    quoteTimer.current = setTimeout(async () => {
      try {
        setQuoting(true);
        const loc = bene.idLocation ? `&idLocation=${bene.idLocation}` : "";
        const { data } = await axios.get(
          `${API}/api/pmi/quote?amount=${value}&sentType=BANK${loc}`
        );
        if (data?.corridorReady === false) {
          setCorridorReady(false);
          setCorridorMsg(data.msg || "Corredor no disponible.");
          setQuote(null);
        } else {
          setQuote(data);
          setLocation(data.location || null);
        }
      } catch (err) {
        console.error(err);
        setQuote(null);
      } finally {
        setQuoting(false);
      }
    }, 400);

    return () => clearTimeout(quoteTimer.current);
  }, [amount, corridorReady, bene.idLocation]);

  /* ---------------------------------------------------------------- *
   * Handlers
   * ---------------------------------------------------------------- */

  const handleAmount = (e) => {
    // digits only, no decimals — same rule as the legacy form
    const digits = e.target.value.replace(/[^\d]/g, "").slice(0, 7);
    setAmount(digits ? fmtEs(Number(digits), 0) : "");
  };

  const handleAccount = (e) => {
    const max = cuenta ? 20 : 11;
    setBene((p) => ({
      ...p,
      bAccnumber: e.target.value.replace(/[^\d]/g, "").slice(0, max),
    }));
  };

  const DOC_TYPES = docStatus?.types || [
    { id: "dni", name: "DNI", sides: 2 },
    { id: "nie", name: "NIE", sides: 2 },
    { id: "per", name: "Permiso de residencia", sides: 2 },
    { id: "pass", name: "Pasaporte", sides: 1 },
  ];

  // Slot count follows what the user says they're uploading, not the free-text
  // doctype captured at registration.
  const backRequired = docType
    ? DOC_TYPES.find((d) => d.id === docType)?.sides !== 1
    : true;

  const handleDocPick = (side) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 8 * 1024 * 1024) {
      toast.error("El archivo supera los 8 MB", { position: "top-center", theme: "dark" });
      e.target.value = "";
      return;
    }

    setPicked((p) => ({ ...p, [side]: file }));
    setPreviews((p) => {
      if (p[side]) URL.revokeObjectURL(p[side]);
      return {
        ...p,
        [side]: file.type.startsWith("image/") ? URL.createObjectURL(file) : "pdf",
      };
    });
    e.target.value = "";
  };

  const uploadOne = async (side, file) => {
    const form = new FormData();
    form.append("document", file);
    form.append("side", side);
    if (docType) form.append("docType", docType);
    const { data } = await axios.post(`${API}/api/pmi/document`, form, {
      headers: { token: Cookies.get("token") }, // browser sets the boundary
    });
    return data;
  };

  const submitDocuments = async () => {
    try {
      setDocUploading(true);

      let last = null;
      if (picked.front) last = await uploadOne("front", picked.front);
      if (picked.back) last = await uploadOne("back", picked.back);

      setDocStatus((prev) => ({
        ...(prev || {}),
        front: last?.front,
        back: last?.back,
        complete: last?.complete,
      }));

      if (!last?.complete) {
        toast.error("Faltan documentos por adjuntar", {
          position: "top-center",
          theme: "dark",
        });
        return;
      }

      toast.success("Documentos enviados", { position: "top-center", theme: "dark" });
      Object.values(previews).forEach((u) => u && u !== "pdf" && URL.revokeObjectURL(u));
      setPreviews({ front: null, back: null });
      setPicked({ front: null, back: null });
      setDocModal(false);
      setConfirmModal(true);
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.msg || "No se pudieron subir los documentos",
        { position: "top-center", theme: "dark" }
      );
    } finally {
      setDocUploading(false);
    }
  };

  // Ready when every side we still owe has a file chosen
  const docsReady =
    Boolean(docType) &&
    (docStatus?.front || picked.front) &&
    (!backRequired || docStatus?.back || picked.back);

  const changeDocType = (id) => {
    if (id === docType) return;
    // Different document -> anything already picked belongs to the old one
    Object.values(previews).forEach((u) => u && u !== "pdf" && URL.revokeObjectURL(u));
    setPreviews({ front: null, back: null });
    setPicked({ front: null, back: null });
    setDocType(id);
  };

  const openDocModal = () => setDocModal(true);

  const closeDocModal = () => {
    Object.values(previews).forEach((u) => u && u !== "pdf" && URL.revokeObjectURL(u));
    setPreviews({ front: null, back: null });
    setPicked({ front: null, back: null });
    setDocModal(false);
  };

  const handleCedula = (e) =>
    setBene((p) => ({ ...p, cedula: e.target.value.replace(/[^\d]/g, "").slice(0, 10) }));

  const profileComplete =
    userdata &&
    userdata.postalcode &&
    userdata.docId &&
    userdata.doctype &&
    userdata.dob;

  // Only actual uploaded files count. `kyc: true` doesn't — that came from
  // Neocheck video ID, which produced no image — and neither does the flat
  // `img` array, whose entries point at files no current flow wrote.
  const hasDocument = Boolean(docStatus?.complete);

  const validate = () => {
    const value = parseEs(amount);

    if (!bene.bname.trim()) return "Indica el nombre del beneficiario";
    if (!bene.cedula.trim()) return "Indica la cédula del beneficiario";
    if (!bene.idLocation) return "Selecciona la entidad bancaria";
    if (cuenta && bene.bAccnumber.length !== 20)
      return "La cuenta debe tener 20 dígitos";
    if (!cuenta && bene.bAccnumber.length !== 11)
      return "El teléfono debe tener 11 dígitos";
    if (value < MIN_AMOUNT) return `Envíos solo a partir de ${MIN_AMOUNT},00€`;
    if (value > MAX_AMOUNT) return `Máximo ${fmtEs(MAX_AMOUNT, 0)},00€ por envío`;
    if (!quote) return "Espera a que se calcule el importe";
    if (!profileComplete)
      return "Necesitas completar tus datos personales en Mis Datos";
    if (!terms) return "Acepta nuestros Términos y Condiciones";

    return null;
  };

  const openConfirm = (e) => {
    e.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error, { position: "top-center", theme: "dark" });
      return;
    }
    // Already have an ID on file (or just uploaded one) -> straight to confirm.
    // Otherwise ask for it once, then continue automatically.
    if (hasDocument) setConfirmModal(true);
    else openDocModal();
  };

  const submitOrder = async () => {
    try {
      setSubmitting(true);

      const { data } = await axios.post(
        `${API}/api/pmi/order`,
        {
          amount: parseEs(amount),
          idLocation: bene.idLocation,
          sentType: "BANK",
          purpose,
          beneficiary: {
            bname: bene.bname.trim(),
            cedula: bene.cedula.trim(),
            bAccnumber: bene.bAccnumber,
            Rphone: bene.Rphone,
          },
        },
        authHeaders()
      );

      if (data?.checkoutUrl) {
        // Hand off to the PSP's hosted checkout
        window.location.href = data.checkoutUrl;
        return;
      }

      // Order exists but no checkout link came back — don't lose it.
      toast.warn(
        "Tu orden se ha creado pero el enlace de pago no está disponible. Consulta tu historial.",
        { position: "top-center", theme: "dark", autoClose: 6000 }
      );
      console.warn("[PMI] no checkoutUrl in response:", data);
      navigate("/orderhistory");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.msg || "No se pudo crear la orden. Inténtalo de nuevo.",
        { position: "top-center", theme: "dark" }
      );
    } finally {
      setSubmitting(false);
      setConfirmModal(false);
    }
  };

  const handleNotifyMe = async () => {
    if (!notifyEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(notifyEmail)) {
      toast.error("Ingresa un email válido", { position: "top-center", theme: "dark" });
      return;
    }
    try {
      setNotifyLoading(true);
      await axios.post(`${API}/api/orders/notify`, { email: notifyEmail });
      setNotifySent(true);
      toast.success("¡Te avisaremos en cuanto estemos disponibles!", {
        position: "top-center",
        theme: "dark",
      });
    } catch (err) {
      toast.error("Hubo un error, intenta de nuevo", {
        position: "top-center",
        theme: "dark",
      });
    } finally {
      setNotifyLoading(false);
    }
  };

  /* ---------------------------------------------------------------- *
   * Render
   * ---------------------------------------------------------------- */

  if (!user) return <Navigate to={"/login"} />;

  const bankLabel =
    banks.find((x) => String(x.id) === String(bene.idLocation))?.label || null;

  const styles = (
    <style>{`
      .tv-pay {
        --accent: #F6394F;
        --accent-strong: #e22440;
        --accent-tint: #fff1f3;
        --ink: #101828;
        --muted: #667085;
        --label: #475467;
        --field: #f5f6f8;
        --line: #ececf0;

        /* One 4px scale. Every margin/padding below picks from these —
           no loose numbers, so spacing stays consistent as things change. */
        --s1: 4px;
        --s2: 8px;
        --s3: 12px;
        --s4: 16px;
        --s5: 20px;
        --s6: 24px;
        --s7: 32px;
        --s8: 70px;

        color: var(--ink);
      }
      .tv-pay * { box-sizing: border-box; }

      .tv-wrap {
        width: 90%;
        margin: var(--s8) auto;
        padding: var(--s7) var(--s5) var(--s8);
        display: grid;
        grid-template-columns: minmax(0, 1fr) 390px;
        gap: var(--s6);
        align-items: start;
      }
      @media (max-width: 900px) {
        .tv-wrap {
         width: 92%;
          grid-template-columns: 1fr;
          margin: var(--s5) auto;
          padding: var(--s5) var(--s4) var(--s7);
          gap: var(--s5);
          margin-top: 60px;
        }
      }

      .tv-title {
        font-size: 1.6rem; font-weight: 700;
        margin: 0 0 var(--s1); letter-spacing: -0.02em;
      }
      .tv-sub {
        color: var(--muted); font-size: 0.92rem;
        margin: 0 0 var(--s6);
      }

      .tv-card {
        background: #fff;
        border: 1px solid var(--line);
        border-radius: 14px;
        padding: var(--s6);
        margin: 0 0 var(--s5);
      }
      .tv-card:last-child { margin-bottom: 0; }
      .tv-card-title {
        font-size: 0.78rem;
        font-weight: 700;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--muted);
        margin: 0 0 var(--s5);
      }

      .tv-field { margin-bottom: var(--s5); }
      .tv-field:last-child { margin-bottom: 0; }
      .tv-label {
        display: block;
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--label);
        margin-bottom: var(--s2);
      }
      .tv-hint {
        font-size: 0.76rem; color: #98a2b3;
        margin: var(--s2) 0 0;
      }

      .tv-pay input.tv-input,
      .tv-pay select.tv-input {
        width: 100%;
        height: 48px;
        background: var(--field);
        border: 1px solid var(--line);
        border-radius: 10px;
        padding: 0 var(--s4);
        font-size: 0.95rem;
        color: var(--ink);
        outline: none;
        transition: border-color .15s, box-shadow .15s, background .15s;
        -webkit-appearance: none;
        appearance: none;
      }
      .tv-pay select.tv-input {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23667085' stroke-width='1.8' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right var(--s4) center;
        padding-right: var(--s8);
      }
      .tv-pay input.tv-input:focus,
      .tv-pay select.tv-input:focus {
        background-color: #fff;
        border-color: var(--accent);
        box-shadow: 0 0 0 3px rgba(246,57,79,.14);
      }
      .tv-pay input.tv-input:disabled { color: var(--ink); -webkit-text-fill-color: var(--ink); opacity: 1; }

      /* amount + currency suffix */
      .tv-combo { display: flex; align-items: stretch; }
      .tv-combo input.tv-input { border-radius: 10px 0 0 10px; border-right: none; }
      .tv-combo .tv-curr {
        display: flex; align-items: center;
        padding: 0 var(--s4);
        background: rgba(16,24,40,.03);
        border: 1px solid var(--line);
        border-left: none;
        border-radius: 0 10px 10px 0;
        font-size: .8rem; font-weight: 700; color: var(--muted);
      }

      /* segmented control for cuenta / pago móvil */
      .tv-seg {
        display: flex; background: var(--field);
        border-radius: 10px; padding: var(--s1); gap: var(--s1);
      }
      .tv-seg button {
        flex: 1; border: none; background: none;
        padding: var(--s3) var(--s2); border-radius: 7px;
        font-size: .87rem; font-weight: 600; color: var(--muted);
        cursor: pointer; transition: background .15s, color .15s, box-shadow .15s;
      }
      .tv-seg button.on {
        background: #fff; color: var(--accent);
        box-shadow: 0 1px 3px rgba(16,24,40,.12);
      }

      /* ---- signature: sticky conversion summary ---- */
      .tv-aside { position: sticky; top: 88px; }
      @media (max-width: 900px) { .tv-aside { position: static; } }

      .tv-summary {
        background: #fff;
        border: 1px solid var(--line);
        border-radius: 14px;
        overflow: hidden;
      }
      .tv-flow { padding: var(--s6); }
      .tv-leg-label {
        font-size: .72rem; font-weight: 700; letter-spacing: .08em;
        text-transform: uppercase; color: var(--muted); margin: 0 0 var(--s2);
      }
      .tv-leg-amt {
        font-size: 1.75rem; font-weight: 700; letter-spacing: -.02em;
        line-height: 1.15; margin: 0;
        font-variant-numeric: tabular-nums;
      }
      .tv-leg-amt.out { color: var(--accent); }
      .tv-leg-cur { font-size: .95rem; font-weight: 600; color: var(--muted); margin-left: var(--s2); }

      /* the connector: a rule with the rate sitting on it */
      .tv-rate-rule {
        display: flex; align-items: center; gap: var(--s3);
        margin: var(--s5) 0;
      }
      .tv-rate-rule::before, .tv-rate-rule::after {
        content: ""; height: 1px; background: var(--line); flex: 1;
      }
      .tv-rate-rule span {
        font-size: .74rem; color: var(--muted); white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }

      .tv-breakdown { border-top: 1px solid var(--line); padding: var(--s5) var(--s6); background: #fcfcfd; }
      .tv-row { display: flex; justify-content: space-between; font-size: .86rem; color: var(--muted); }
      .tv-row + .tv-row { margin-top: var(--s3); }
      .tv-row b { color: var(--ink); font-variant-numeric: tabular-nums; }
      .tv-row.total { margin-top: var(--s4); padding-top: var(--s4); border-top: 1px solid var(--line); font-size: .95rem; }
      .tv-row.total b { font-size: 1.1rem; }

      .tv-cta {
        width: 100%; margin-top: var(--s5); padding: var(--s4);
        background: var(--accent); color: #fff;
        font-weight: 700; font-size: .98rem;
        border: none; border-radius: 11px; cursor: pointer;
        transition: background .15s, transform .05s;
      }
      .tv-cta:hover:not(:disabled) { background: var(--accent-strong); }
      .tv-cta:active:not(:disabled) { transform: translateY(1px); }
      .tv-cta:disabled { opacity: .5; cursor: not-allowed; }

      .tv-note {
        display: flex; gap: var(--s2); margin-top: var(--s4);
        font-size: .78rem; color: var(--muted); line-height: 1.5;
      }

      .tv-alert {
        display: flex; gap: var(--s3); align-items: flex-start;
        background: #fffaeb; border: 1px solid #fedf89;
        border-radius: 10px; padding: var(--s4);
        font-size: .87rem; color: #93370d; margin-bottom: var(--s5);
        line-height: 1.5;
      }
      .tv-alert a { color: #93370d; font-weight: 700; cursor: pointer; }

      .tv-link {
        background: none; border: none; padding: 0;
        color: var(--accent); font-size: .82rem; font-weight: 600; cursor: pointer;
      }

      .tv-check {
        display: flex; gap: var(--s3); align-items: flex-start;
        font-size: .85rem; color: var(--label); line-height: 1.5;
        margin-top: var(--s5);
      }
      .tv-check input {
        width: 18px; height: 18px; margin-top: 1px;
        flex-shrink: 0; accent-color: var(--accent); cursor: pointer;
      }

      /* modals — App.css forces .OrderSection .modal-dialog to 93% */
      .OrderSection .modal-dialog.tv-dlg { max-width: 440px !important; width: 100% !important; }
      .OrderSection .modal-dialog.tv-dlg-wide { max-width: 560px !important; width: 100% !important; }
      .tv-dlg .modal-content, .tv-dlg-wide .modal-content {
        border: none; border-radius: 14px;
        box-shadow: 0 16px 48px rgba(16,24,40,.2);
      }
      .tv-dlg .modal-header, .tv-dlg-wide .modal-header { padding: var(--s5) var(--s6); border-bottom: 1px solid var(--line); }
      .tv-dlg .modal-body, .tv-dlg-wide .modal-body { padding: var(--s6); font-size: .92rem; }
      .tv-dlg .modal-footer, .tv-dlg-wide .modal-footer { padding: var(--s5) var(--s6); border-top: 1px solid var(--line); gap: var(--s3); }
      @media (max-width: 560px) {
        .OrderSection .modal-dialog.tv-dlg,
        .OrderSection .modal-dialog.tv-dlg-wide { max-width: 92% !important; }
      }

      .tv-pill {
        border: 1px solid #d0d5dd; background: #fff; color: var(--label);
        border-radius: 9px; padding: var(--s3) var(--s4); font-size: .85rem; font-weight: 500;
        cursor: pointer; transition: all .15s;
      }
      .tv-pill.on {
        border: 2px solid var(--accent); background: var(--accent-tint);
        color: var(--accent); font-weight: 700;
      }
      .tv-pill small { opacity: .7; font-weight: 400; margin-left: var(--s2); font-size: .72rem; }

      .tv-drop {
        border: 2px dashed #d0d5dd; border-radius: 12px; height: 160px;
        gap: var(--s2);
        display: flex; flex-direction: column; align-items: center; justify-content: center;
        background: #fafafa; cursor: pointer; overflow: hidden; transition: all .15s;
      }
      .tv-drop.filled { border: 2px solid #12b76a; background: #f6fef9; }
      .tv-drop img { width: 100%; height: 100%; object-fit: cover; }

      @media (prefers-reduced-motion: reduce) {
        .tv-pay *, .tv-pay *::before, .tv-pay *::after {
          transition: none !important; animation: none !important;
        }
      }
    `}</style>
  );

  if (bootLoading) {
    return (
      <div className="OrderSection tv-pay" style={{ minHeight: "100vh" }}>
        {styles}
        <Navbar />
        <img src={loader} alt="Cargando" className="loader mt-5" />
      </div>
    );
  }

  // Corridor not provisioned yet
  if (!corridorReady) {
    return (
      <div className="OrderSection tv-pay" style={{ minHeight: "100vh" }}>
        {styles}
        <Navbar />
        <div
          className="modal_bg d-flex justify-content-center align-items-center"
          style={{ position: "fixed", inset: 0, zIndex: 9999 }}
        >
          <div className="modal d-block" tabIndex="-1" style={{ position: "relative" }}>
            <div className="modal-dialog tv-dlg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5">Servicio temporalmente pausado</h1>
                </div>
                <div className="modal-body">
                  <p className="mb-3">{corridorMsg}</p>
                  {!notifySent ? (
                    <>
                      {!userdata?.email && (
                        <input
                          type="email"
                          className="tv-input mb-2"
                          placeholder="Tu correo electrónico"
                          value={notifyEmail}
                          onChange={(e) => setNotifyEmail(e.target.value)}
                        />
                      )}
                      <button
                        type="button"
                        className="tv-cta"
                        style={{ marginTop: 0 }}
                        onClick={handleNotifyMe}
                        disabled={notifyLoading}
                      >
                        {notifyLoading ? "Enviando…" : "Avisarme cuando esté disponible"}
                      </button>
                    </>
                  ) : (
                    <p className="mb-0" style={{ color: "#12b76a", fontWeight: 600 }}>
                      Te avisaremos por correo en cuanto reactivemos el servicio.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="OrderSection tv-pay">
      {styles}
      <Navbar />

      <form onSubmit={openConfirm} className="tv-wrap">
        {/* ================= LEFT: the form ================= */}
        <div>
          <h1 className="tv-title">Enviar dinero a Venezuela</h1>
          <p className="tv-sub">
            El dinero llega a la cuenta del beneficiario en bolívares.
          </p>
          <br></br>
          {!profileComplete && (
            <div className="tv-alert">
              <AiFillWarning size={18} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Completa tus datos personales en{" "}
                <a onClick={() => navigate("/profile")}>Mis Datos</a> antes de enviar.
              </span>
            </div>
          )}

          {/* ---- beneficiario ---- */}
          <div className="tv-card">
            <p className="tv-card-title">Quién recibe</p>

            <div className="tv-field">
              <label className="tv-label">Nombre y apellido</label>
              <input
                className="tv-input"
                type="text"
                value={bene.bname}
                onChange={(e) => setBene((p) => ({ ...p, bname: e.target.value }))}
                autoComplete="off"
                placeholder="Como aparece en su cédula"
                required
              />
              {savedBenes.length > 0 && (
                <button type="button" className="tv-link mt-2" onClick={() => setBeneModal(true)}>
                  Elegir de mi agenda
                </button>
              )}
            </div>

            <div className="tv-field">
              <label className="tv-label">Cédula</label>
              <input
                className="tv-input"
                type="text"
                value={bene.cedula}
                onChange={handleCedula}
                placeholder='Indicar "V" o "E"'
                required
              />
            </div>

            <div className="tv-field">
              <label className="tv-label">Banco</label>
              <select
                className="tv-input"
                value={bene.idLocation}
                onChange={(e) => setBene((p) => ({ ...p, idLocation: e.target.value }))}
                required
              >
                <option value="">Elige el banco</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.code ? `${b.code} — ${b.label}` : b.label}
                  </option>
                ))}
              </select>
              {banks.length === 0 && (
                <p className="tv-hint" style={{ color: "var(--accent)" }}>
                  No hay bancos disponibles ahora mismo.
                </p>
              )}
            </div>

            <div className="tv-field">
              <label className="tv-label">Cómo recibe el dinero</label>
              <div className="tv-seg">
                <button
                  type="button"
                  className={cuenta ? "on" : ""}
                  onClick={() => {
                    setCuenta(true);
                    setBene((p) => ({ ...p, bAccnumber: "" }));
                  }}
                >
                  Cuenta bancaria
                </button>
                <button
                  type="button"
                  className={!cuenta ? "on" : ""}
                  onClick={() => {
                    setCuenta(false);
                    setBene((p) => ({ ...p, bAccnumber: "" }));
                  }}
                >
                  Pago móvil
                </button>
              </div>
              <input
                className="tv-input mt-2"
                type="text"
                inputMode="numeric"
                value={bene.bAccnumber}
                onChange={handleAccount}
                placeholder={cuenta ? "20 dígitos" : "11 dígitos"}
                required
              />
              <p className="tv-hint">
                {bene.bAccnumber.length}/{cuenta ? 20 : 11} dígitos
              </p>
            </div>

          </div>

          {/* ---- importe ---- */}
          <div className="tv-card">
            <p className="tv-card-title">Cuánto envías</p>

            <div className="tv-field">
              <label className="tv-label">Importe</label>
              <div className="tv-combo">
                <input
                  className="tv-input"
                  type="text"
                  inputMode="numeric"
                  value={amount}
                  onChange={handleAmount}
                  placeholder="0"
                  required
                />
                <div className="tv-curr">EUR</div>
              </div>
              <p className="tv-hint">
                Entre {MIN_AMOUNT},00 € y {fmtEs(MAX_AMOUNT, 0)},00 €. Sin decimales.
              </p>
            </div>

            <div className="tv-field">
              <label className="tv-label">Motivo del envío</label>
              <select
                className="tv-input"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
              >
                {(purposes.length ? purposes : [{ id: "fami", name: "Apoyo familiar" }]).map(
                  (p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  )
                )}
              </select>
              <p className="tv-hint">Nos lo exige la normativa de prevención de blanqueo.</p>
            </div>
          </div>
        </div>


        {/* ================= RIGHT: sticky summary ================= */}
        <aside className="tv-aside">
          <div className="tv-summary">
            <div className="tv-flow">
              <p className="tv-leg-label">Envías</p>
              <p className="tv-leg-amt">
                {quote ? fmtEs(quote.gross) : "0,00"}
                <span className="tv-leg-cur">EUR</span>
              </p>

              <div className="tv-rate-rule">
                <span>
                  {quote
                    ? `1 EUR = ${fmtEs(quote.fxRate, 4)} ${quote.currencyTo}`
                    : "Escribe un importe"}
                </span>
              </div>

              <p className="tv-leg-label">Recibe</p>
              <p className="tv-leg-amt out">
                {quoting ? "…" : quote ? fmtEs(quote.receives) : "0,00"}
                <span className="tv-leg-cur">{quote?.currencyTo || "VES"}</span>
              </p>
            </div>

            <div className="tv-breakdown">
              <div className="tv-row">
                <span>Comisión</span>
                <b>{quote ? fmtEs(quote.fee) : "0,00"} €</b>
              </div>
              {bankLabel && (
                <div className="tv-row">
                  <span>Banco destino</span>
                  <b>{bankLabel}</b>
                </div>
              )}
              <div className="tv-row total">
                <span>Total a pagar</span>
                <b>{quote ? fmtEs(quote.gross) : "0,00"} €</b>
              </div>
            </div>
          </div>

          <label className="tv-check">
            <input
              type="checkbox"
              checked={terms}
              onChange={(e) => setTerms(e.target.checked)}
            />
            <span>
              He leído y acepto los{" "}
              <a href="/terms" target="_blank" rel="noreferrer">
                Términos y Condiciones
              </a>
              .
            </span>
          </label>

          <button
            type="submit"
            className="tv-cta"
            disabled={submitting || quoting || docUploading || !quote}
          >
            {submitting ? "Procesando…" : "Continuar al pago"}
          </button>

          <p className="tv-note">
            Pagas en la pasarela segura de nuestro proveedor. Te llevaremos allí al
            confirmar.
          </p>
        </aside>
      </form>

      {/* ============ agenda ============ */}
      {beneModal && (
        <div
          className="modal_bg d-flex justify-content-center align-items-center"
          style={{ position: "fixed", inset: 0, zIndex: 9999 }}
        >
          <div className="modal d-block" tabIndex="-1" style={{ position: "relative" }}>
            <div className="modal-dialog tv-dlg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Mi agenda</h5>
                  <button type="button" className="btn-close" onClick={() => setBeneModal(false)} />
                </div>
                <div className="modal-body" style={{ maxHeight: 360, overflowY: "auto" }}>
                  {savedBenes.map((b) => (
                    <button
                      key={b.id}
                      type="button"
                      className="w-100 text-start"
                      style={{
                        border: "1px solid var(--line)",
                        borderRadius: 10,
                        padding: "12px 14px",
                        marginBottom: 8,
                        background: "#fff",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        // Legacy beneficiaries stored the bank as free text
                        // ("BANESCO"). Match it back to a PMI location id.
                        const match = banks.find((x) =>
                          x.label
                            .toLowerCase()
                            .includes(String(b.bbank || "").toLowerCase().trim())
                        );
                        setBene({
                          bname: b.bname || "",
                          cedula: b.cedula || "",
                          idLocation: match ? String(match.id) : "",
                          bAccnumber: b.bAccnumber || "",
                          Rphone: "",
                        });
                        setCuenta(String(b.bAccnumber || "").length === 20);
                        setBeneModal(false);
                      }}
                    >
                      <div style={{ fontWeight: 600 }}>{b.bname}</div>
                      <div style={{ fontSize: ".8rem", color: "var(--muted)" }}>
                        {b.bbank} · {b.bAccnumber}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setBeneModal(false)}>
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ documento ============ */}
      {docModal && (
        <div
          className="modal_bg d-flex justify-content-center align-items-center"
          style={{ position: "fixed", inset: 0, zIndex: 9999 }}
        >
          <div className="modal d-block" tabIndex="-1" style={{ position: "relative" }}>
            <div className="modal-dialog tv-dlg-wide modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5">Tu documento de identidad</h1>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeDocModal}
                    disabled={docUploading}
                  />
                </div>

                <div className="modal-body">
                  <p className="tv-label" style={{ marginBottom: 10 }}>
                    ¿Qué documento vas a adjuntar?
                  </p>

                  <div className="d-flex flex-wrap gap-2 mb-4">
                    {DOC_TYPES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        className={`tv-pill${docType === t.id ? " on" : ""}`}
                        onClick={() => changeDocType(t.id)}
                        disabled={docUploading}
                      >
                        {t.name}
                        <small>{t.sides === 1 ? "1 cara" : "2 caras"}</small>
                      </button>
                    ))}
                  </div>

                  <input
                    ref={frontInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="d-none"
                    onChange={handleDocPick("front")}
                  />
                  <input
                    ref={backInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    className="d-none"
                    onChange={handleDocPick("back")}
                  />

                  {!docType ? (
                    <p className="tv-hint text-center py-4 mb-0">
                      Elige un tipo de documento para continuar.
                    </p>
                  ) : (
                    <div className="row g-3">
                      {[
                        {
                          side: "front",
                          label: backRequired ? "Anverso" : "Página de datos",
                          hint: "La cara con tu foto",
                          ref: frontInput,
                        },
                        ...(backRequired
                          ? [{ side: "back", label: "Reverso", hint: "La cara posterior", ref: backInput }]
                          : []),
                      ].map(({ side, label, hint, ref }) => {
                        const done = docStatus?.[side];
                        const preview = previews[side];
                        const filled = Boolean(preview || done);
                        return (
                          <div className={backRequired ? "col-6" : "col-12"} key={side}>
                            <div
                              className={`tv-drop${filled ? " filled" : ""}`}
                              onClick={() => !docUploading && ref.current?.click()}
                            >
                              {preview && preview !== "pdf" ? (
                                <img src={preview} alt={label} />
                              ) : (
                                <>
                                  <span style={{ fontSize: 24, lineHeight: 1, color: filled ? "#12b76a" : "#98a2b3" }}>
                                    {filled ? "✓" : "+"}
                                  </span>
                                  <span style={{ fontWeight: 600, fontSize: ".9rem", marginTop: 8 }}>
                                    {label}
                                  </span>
                                  <span style={{ fontSize: ".76rem", color: "var(--muted)" }}>
                                    {preview === "pdf"
                                      ? "PDF adjunto"
                                      : done
                                        ? "Ya en tu cuenta"
                                        : hint}
                                  </span>
                                </>
                              )}
                            </div>
                            <p
                              className="text-center mt-1 mb-0"
                              style={{ fontSize: ".76rem", color: filled ? "#12b76a" : "#98a2b3" }}
                            >
                              {filled ? "Toca para cambiar" : "Toca para adjuntar"}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {docType && (
                    <p className="tv-hint text-center mt-3 mb-0">
                      JPG, PNG, WEBP o PDF. Máximo 8 MB por archivo.
                    </p>
                  )}
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={closeDocModal}
                    disabled={docUploading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="tv-cta ms-2"
                    style={{ width: "auto", marginTop: 0, padding: "11px 22px" }}
                    onClick={submitDocuments}
                    disabled={!docsReady || docUploading}
                  >
                    {docUploading ? "Enviando…" : "Enviar y continuar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ confirmar ============ */}
      {confirmModal && quote && (
        <div
          className="modal_bg d-flex justify-content-center align-items-center"
          style={{ position: "fixed", inset: 0, zIndex: 9999 }}
        >
          <div className="modal d-block" tabIndex="-1" style={{ position: "relative" }}>
            <div className="modal-dialog tv-dlg modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h1 className="modal-title fs-5">Revisa tu envío</h1>
                </div>
                <div className="modal-body">
                  <div className="tv-row"><span>Beneficiario</span><b>{bene.bname}</b></div>
                  <div className="tv-row"><span>Cédula</span><b>{bene.cedula}</b></div>
                  <div className="tv-row"><span>Banco</span><b>{bankLabel || "—"}</b></div>
                  <div className="tv-row">
                    <span>{cuenta ? "Cuenta" : "Pago móvil"}</span>
                    <b>{bene.bAccnumber}</b>
                  </div>

                  <div className="tv-rate-rule">
                    <span>1 EUR = {fmtEs(quote.fxRate, 4)} {quote.currencyTo}</span>
                  </div>

                  <div className="tv-row"><span>Comisión</span><b>{fmtEs(quote.fee)} €</b></div>
                  <div className="tv-row">
                    <span>Recibe</span>
                    <b style={{ color: "var(--accent)" }}>
                      {fmtEs(quote.receives)} {quote.currencyTo}
                    </b>
                  </div>
                  <div className="tv-row total"><span>Total a pagar</span><b>{fmtEs(quote.gross)} €</b></div>

                  <p className="tv-hint mt-3 mb-0">
                    Al continuar te llevaremos a la pasarela de pago segura.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setConfirmModal(false)}
                    disabled={submitting}
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    className="tv-cta ms-2"
                    style={{ width: "auto", marginTop: 0, padding: "11px 22px" }}
                    onClick={submitOrder}
                    disabled={submitting}
                  >
                    {submitting ? "Procesando…" : "Confirmar y pagar"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payment;